/**
 * Locale table for the document-source viewer — the `.docx` / `.hwpx` "보기"
 * leg of the source-verification axiom, mounted inside the IDE Design/Source
 * surface and on `/studio/import`.
 *
 * WHY THIS EXISTS: this component already had TWO locale tables inline (the
 * honest-label badge and the retention notice) while ~29 other creator-facing
 * strings on the same panel stayed English literals — and one string had
 * already drifted into a half-Korean literal (`"{reason}. 고유 anchor를 선택해
 * 이동하세요:"`), which is what a partly-wired surface eventually produces.
 * Both inline tables are folded in here so there is one table per surface,
 * not three.
 *
 * NOT LOCALIZED, deliberately — and asserted as such by the i18n gate:
 *   - format tokens `.docx` / `.hwpx` / `DOCX` / `HWPX` / `WYSIWYG`, and the
 *     structural anchors themselves (`sec0:para:N`, `body:para:N`).
 *   - values produced by `@cogo/source-document`: the stat tile labels and the
 *     locator `reason` sentences. They belong to that package, not to this
 *     surface; their slots carry `data-i18n-owner="@cogo/source-document"` so
 *     the residue gate reports them as that package's remaining work instead
 *     of silently covering for them here.
 */
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

/** Closed enum from `@cogo/source-document`'s locator. */
export type DocxMatchedBy =
  | "anchor"
  | "paragraph-ordinal"
  | "heading-path"
  | "heading-title";

export interface DocxSourceViewerCopy {
  /** The always-on badge: this is a STRUCTURE view, not a rendering. */
  honestLabel: string;
  /** report.status.ready / report.status.missing — headers only, not new sentences. */
  capabilitiesNow: string;
  capabilitiesNotYet: string;
  retention: {
    /** The write completed: it comes back after a reload. */
    stored: string;
    /** The write is still in flight. */
    checking: string;
    /** The browser refused the size. */
    notStoredQuota: string;
    /** The browser did not allow storing at all (private mode, blocked, …). */
    notStoredOther: string;
  };
  header: {
    title: string;
    importDocument: string;
    replaceDocument: string;
    parsing: string;
    remove: string;
  };
  restoringNotice: string;
  error: {
    couldNotRead: (fileName: string) => string;
    failClosed: string;
  };
  hwpxCoverage: string;
  unknownElements: string;
  locator: {
    placeholderHwpx: string;
    placeholderDocx: string;
    dereference: string;
    resolvedTo: (anchor: string, matchedBy: string) => string;
    matchedBy: Record<DocxMatchedBy, string>;
    /** Follows the package's own `reason` sentence. */
    ambiguousInstruction: string;
    moreCandidates: (count: number) => string;
    gapPrefix: string;
    gapNoHighlight: string;
  };
  pagination: {
    prev: string;
    next: string;
  };
  empty: {
    /** `extensions` is the joined ".docx or .hwpx" token — never translated. */
    title: (extensions: string) => string;
    body: string;
    action: string;
  };
  block: {
    listBadge: (level: number) => string;
    numBadge: (numId: number) => string;
    headingBadge: (level: number) => string;
    emptyParagraph: string;
    kind: Record<string, string>;
  };
}

const en: DocxSourceViewerCopy = {
  honestLabel:
    "Normalized structure view — original formatting and layout are not reproduced (not WYSIWYG)",
  capabilitiesNow: "Works now",
  capabilitiesNotYet: "Not yet",
  retention: {
    stored:
      "This document is saved in this browser. It stays open as you switch views and after a page reload.",
    checking: "Saving it in this browser.",
    notStoredQuota:
      "There was not enough room to save it in this browser. It stays as you switch views, but a page reload means opening it again.",
    notStoredOther:
      "This browser did not allow saving it. It stays as you switch views, but a page reload means opening it again.",
  },
  header: {
    title: "Document source",
    importDocument: "Import document",
    replaceDocument: "Replace document",
    parsing: "Parsing",
    remove: "Remove",
  },
  restoringNotice: "Reopening the document saved in this browser…",
  error: {
    couldNotRead: (fileName) => `Could not read ${fileName}`,
    failClosed:
      "Fail-closed: no partial structure is shown for an unreadable source.",
  },
  hwpxCoverage:
    "HWPX extraction measures these five counts. Hidden characters, field codes, image references and alternate-content branches are not measured for this format, so they are not shown.",
  unknownElements: "Unknown elements (counted, not dropped):",
  locator: {
    placeholderHwpx: "Locator: anchor (sec0:para:N) or heading text",
    placeholderDocx:
      "Locator: paragraph ordinal, anchor (body:para:N), or heading text",
    dereference: "Dereference",
    resolvedTo: (anchor, matchedBy) =>
      `Resolved to ${anchor} (matched by ${matchedBy}).`,
    matchedBy: {
      anchor: "anchor",
      "paragraph-ordinal": "paragraph ordinal",
      "heading-path": "heading path",
      "heading-title": "heading title",
    },
    ambiguousInstruction: "Pick a unique anchor to jump to:",
    moreCandidates: (count) => `+${count} more`,
    gapPrefix: "Gap:",
    gapNoHighlight: "No block was highlighted.",
  },
  pagination: { prev: "Prev", next: "Next" },
  empty: {
    title: (extensions) =>
      `Import a ${extensions} document to view its normalized structure`,
    body: "The file is parsed locally in your browser; nothing is uploaded. Use it to read (extract) and see (view) a source before trusting the ontology derived from it.",
    action: "Choose a document",
  },
  block: {
    listBadge: (level) => `list L${level}`,
    numBadge: (numId) => ` · num ${numId}`,
    headingBadge: (level) => `H${level}`,
    emptyParagraph: "(empty paragraph)",
    kind: { paragraph: "paragraph", table: "table" },
  },
};

const ko: DocxSourceViewerCopy = {
  honestLabel: "정규화 구조 뷰 — 원본 서식/레이아웃 재현 아님 (WYSIWYG 아님)",
  capabilitiesNow: "준비됨",
  capabilitiesNotYet: "아직 없음",
  retention: {
    stored:
      "이 문서는 이 브라우저에 저장되어 있습니다. 화면을 옮기거나 새로고침해도 그대로 열려 있습니다.",
    checking: "이 브라우저에 저장하는 중입니다.",
    notStoredQuota:
      "이 브라우저에 저장할 공간이 부족해 저장하지 못했습니다. 화면을 옮겨도 유지되지만, 새로고침하면 다시 열어야 합니다.",
    notStoredOther:
      "이 브라우저가 저장을 허용하지 않아 저장하지 못했습니다. 화면을 옮겨도 유지되지만, 새로고침하면 다시 열어야 합니다.",
  },
  header: {
    title: "문서 소스",
    importDocument: "문서 가져오기",
    replaceDocument: "문서 교체",
    parsing: "분석 중",
    remove: "제거",
  },
  restoringNotice: "이 브라우저에 저장된 문서를 다시 여는 중…",
  error: {
    couldNotRead: (fileName) => `${fileName}을(를) 읽지 못했습니다`,
    failClosed:
      "실패 시 차단: 읽을 수 없는 소스는 일부 구조도 보여주지 않습니다.",
  },
  hwpxCoverage:
    "HWPX 추출은 이 다섯 가지 수치만 측정합니다. 숨은 문자, 필드 코드, 이미지 참조, 대체 콘텐츠 분기는 이 형식에서 측정하지 않으므로 표시하지 않습니다.",
  unknownElements: "알 수 없는 요소 (버리지 않고 집계함):",
  locator: {
    placeholderHwpx: "로케이터: anchor (sec0:para:N) 또는 제목 텍스트",
    placeholderDocx:
      "로케이터: 문단 번호, anchor (body:para:N), 또는 제목 텍스트",
    dereference: "찾아가기",
    resolvedTo: (anchor, matchedBy) =>
      `${anchor}(으)로 확인했습니다 (일치 기준: ${matchedBy}).`,
    matchedBy: {
      anchor: "anchor",
      "paragraph-ordinal": "문단 번호",
      "heading-path": "제목 경로",
      "heading-title": "제목",
    },
    ambiguousInstruction: "고유 anchor를 선택해 이동하세요:",
    moreCandidates: (count) => `+${count}개 더`,
    gapPrefix: "공백:",
    gapNoHighlight: "강조 표시된 블록이 없습니다.",
  },
  pagination: { prev: "이전", next: "다음" },
  empty: {
    title: (extensions) =>
      `${extensions} 문서를 가져오면 정규화된 구조를 볼 수 있습니다`,
    body: "파일은 브라우저 안에서만 분석되며 어디에도 업로드되지 않습니다. 여기서 도출한 온톨로지를 신뢰하기 전에, 소스를 읽고(추출) 보는(뷰) 데 사용하세요.",
    action: "문서 선택",
  },
  block: {
    listBadge: (level) => `목록 L${level}`,
    numBadge: (numId) => ` · 번호 ${numId}`,
    headingBadge: (level) => `H${level}`,
    emptyParagraph: "(빈 문단)",
    kind: { paragraph: "문단", table: "표" },
  },
};

const ja: DocxSourceViewerCopy = {
  honestLabel:
    "正規化された構造ビュー — 元の書式やレイアウトは再現しません（WYSIWYG ではありません）",
  capabilitiesNow: "準備完了",
  capabilitiesNotYet: "未取得",
  retention: {
    stored:
      "このドキュメントはこのブラウザに保存されています。画面を切り替えても再読み込みしても開いたままです。",
    checking: "このブラウザに保存しています。",
    notStoredQuota:
      "このブラウザの保存容量が足りず、保存できませんでした。画面を切り替えても保持されますが、再読み込みすると開き直しが必要です。",
    notStoredOther:
      "このブラウザが保存を許可しないため、保存できませんでした。画面を切り替えても保持されますが、再読み込みすると開き直しが必要です。",
  },
  header: {
    title: "ドキュメントソース",
    importDocument: "ドキュメントを取り込む",
    replaceDocument: "ドキュメントを差し替える",
    parsing: "解析中",
    remove: "削除",
  },
  restoringNotice: "このブラウザに保存されたドキュメントを開き直しています…",
  error: {
    couldNotRead: (fileName) => `${fileName} を読み取れませんでした`,
    failClosed:
      "フェイルクローズド: 読み取れないソースは部分的な構造も表示しません。",
  },
  hwpxCoverage:
    "HWPX の抽出はこの 5 つの件数のみを測定します。隠し文字、フィールドコード、画像参照、代替コンテンツの分岐はこの形式では測定しないため表示されません。",
  unknownElements: "未知の要素（破棄せず集計）:",
  locator: {
    placeholderHwpx: "ロケーター: anchor (sec0:para:N) または見出しテキスト",
    placeholderDocx:
      "ロケーター: 段落番号、anchor (body:para:N)、または見出しテキスト",
    dereference: "参照する",
    resolvedTo: (anchor, matchedBy) =>
      `${anchor} に解決しました（一致基準: ${matchedBy}）。`,
    matchedBy: {
      anchor: "anchor",
      "paragraph-ordinal": "段落番号",
      "heading-path": "見出しパス",
      "heading-title": "見出し",
    },
    ambiguousInstruction: "一意の anchor を選んで移動してください:",
    moreCandidates: (count) => `他 ${count} 件`,
    gapPrefix: "ギャップ:",
    gapNoHighlight: "強調表示されたブロックはありません。",
  },
  pagination: { prev: "前へ", next: "次へ" },
  empty: {
    title: (extensions) =>
      `${extensions} のドキュメントを取り込むと、正規化された構造を確認できます`,
    body: "ファイルはブラウザ内でのみ解析され、どこにもアップロードされません。ここから導いたオントロジーを信頼する前に、ソースを読み（抽出）、見る（ビュー）ために使ってください。",
    action: "ドキュメントを選ぶ",
  },
  block: {
    listBadge: (level) => `リスト L${level}`,
    numBadge: (numId) => ` · 番号 ${numId}`,
    headingBadge: (level) => `H${level}`,
    emptyParagraph: "（空の段落）",
    kind: { paragraph: "段落", table: "表" },
  },
};

const zh: DocxSourceViewerCopy = {
  honestLabel: "规范化结构视图 — 不复现原始格式或布局（非 WYSIWYG）",
  capabilitiesNow: "已就绪",
  capabilitiesNotYet: "尚无",
  retention: {
    stored: "该文档已保存在此浏览器中。切换视图或刷新后仍会保持打开。",
    checking: "正在保存到此浏览器。",
    notStoredQuota:
      "此浏览器的存储空间不足，未能保存。切换视图不会丢失，但刷新后需要重新打开。",
    notStoredOther:
      "此浏览器不允许保存，未能保存。切换视图不会丢失，但刷新后需要重新打开。",
  },
  header: {
    title: "文档来源",
    importDocument: "导入文档",
    replaceDocument: "替换文档",
    parsing: "正在解析",
    remove: "移除",
  },
  restoringNotice: "正在重新打开保存在此浏览器中的文档…",
  error: {
    couldNotRead: (fileName) => `无法读取 ${fileName}`,
    failClosed: "失败即阻断：无法读取的来源不会显示任何部分结构。",
  },
  hwpxCoverage:
    "HWPX 提取只测量这五项计数。隐藏字符、域代码、图片引用和备用内容分支在此格式中未被测量，因此不显示。",
  unknownElements: "未知元素（已计数，未丢弃）:",
  locator: {
    placeholderHwpx: "定位符: anchor (sec0:para:N) 或标题文本",
    placeholderDocx: "定位符: 段落序号、anchor (body:para:N) 或标题文本",
    dereference: "解析定位",
    resolvedTo: (anchor, matchedBy) =>
      `已解析到 ${anchor}（匹配依据: ${matchedBy}）。`,
    matchedBy: {
      anchor: "anchor",
      "paragraph-ordinal": "段落序号",
      "heading-path": "标题路径",
      "heading-title": "标题",
    },
    ambiguousInstruction: "请选择唯一的 anchor 以跳转:",
    moreCandidates: (count) => `另有 ${count} 个`,
    gapPrefix: "缺口:",
    gapNoHighlight: "没有高亮任何区块。",
  },
  pagination: { prev: "上一页", next: "下一页" },
  empty: {
    title: (extensions) => `导入 ${extensions} 文档以查看其规范化结构`,
    body: "文件只在你的浏览器中本地解析，不会上传。在信任由它推导出的本体之前，先用它来读取（提取）和查看（视图）来源。",
    action: "选择文档",
  },
  block: {
    listBadge: (level) => `列表 L${level}`,
    numBadge: (numId) => ` · 编号 ${numId}`,
    headingBadge: (level) => `H${level}`,
    emptyParagraph: "（空段落）",
    kind: { paragraph: "段落", table: "表格" },
  },
};

const zhTW: DocxSourceViewerCopy = {
  honestLabel: "正規化結構檢視 — 不重現原始格式或版面（非 WYSIWYG）",
  capabilitiesNow: "已就緒",
  capabilitiesNotYet: "尚無",
  retention: {
    stored: "此文件已儲存在這個瀏覽器中。切換檢視或重新整理後仍會保持開啟。",
    checking: "正在儲存到這個瀏覽器。",
    notStoredQuota:
      "這個瀏覽器的儲存空間不足，未能儲存。切換檢視不會遺失，但重新整理後需要重新開啟。",
    notStoredOther:
      "這個瀏覽器不允許儲存，未能儲存。切換檢視不會遺失，但重新整理後需要重新開啟。",
  },
  header: {
    title: "文件來源",
    importDocument: "匯入文件",
    replaceDocument: "更換文件",
    parsing: "正在解析",
    remove: "移除",
  },
  restoringNotice: "正在重新開啟儲存在這個瀏覽器中的文件…",
  error: {
    couldNotRead: (fileName) => `無法讀取 ${fileName}`,
    failClosed: "失敗即阻斷：無法讀取的來源不會顯示任何部分結構。",
  },
  hwpxCoverage:
    "HWPX 擷取只測量這五項計數。隱藏字元、功能變數代碼、圖片參照與替代內容分支在此格式中未被測量，因此不會顯示。",
  unknownElements: "未知元素（已計數，未丟棄）:",
  locator: {
    placeholderHwpx: "定位符: anchor (sec0:para:N) 或標題文字",
    placeholderDocx: "定位符: 段落序號、anchor (body:para:N) 或標題文字",
    dereference: "解析定位",
    resolvedTo: (anchor, matchedBy) =>
      `已解析到 ${anchor}（比對依據: ${matchedBy}）。`,
    matchedBy: {
      anchor: "anchor",
      "paragraph-ordinal": "段落序號",
      "heading-path": "標題路徑",
      "heading-title": "標題",
    },
    ambiguousInstruction: "請選擇唯一的 anchor 以跳轉:",
    moreCandidates: (count) => `另有 ${count} 個`,
    gapPrefix: "缺口:",
    gapNoHighlight: "沒有標示任何區塊。",
  },
  pagination: { prev: "上一頁", next: "下一頁" },
  empty: {
    title: (extensions) => `匯入 ${extensions} 文件以檢視其正規化結構`,
    body: "檔案只在你的瀏覽器中本機解析，不會上傳。在信任由它推導出的本體之前，先用它來讀取（擷取）與檢視（檢視）來源。",
    action: "選擇文件",
  },
  block: {
    listBadge: (level) => `清單 L${level}`,
    numBadge: (numId) => ` · 編號 ${numId}`,
    headingBadge: (level) => `H${level}`,
    emptyParagraph: "（空段落）",
    kind: { paragraph: "段落", table: "表格" },
  },
};

const DOCX_SOURCE_VIEWER_COPY: Record<LocaleCode, DocxSourceViewerCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getDocxSourceViewerCopy(locale: string): DocxSourceViewerCopy {
  return DOCX_SOURCE_VIEWER_COPY[locale as LocaleCode] ?? en;
}
